import {
  EventsResponse,
  PartnersResponse,
  LocationsResponse,
  EventCategoriesResponse,
  VectorSearchRequest,
  VectorSearchResponse,
  DiscoverEventsResponse,
  Event,
  EventInfo,
  SystemInsights,
  EventFilters,
  LocationSuggestion,
  EntitySuggestion,
  ResolveEntityResponse,
  ResolveLocationResponse,
  TopCity,
  TopCitiesResponse,
  SearchSuggestionsResponse,
  AppMetadata,
  EventGroup,
  EventGroupChallenge,
  CreateEventGroupRequest,
  CreateEventGroupResponse,
  EventGroupEventsResponse,
  EventGroupMapResponse,
  ForgetPastRememberedEventsResponse,
  RememberedEventEventsResponse,
  RememberedEventIdsResponse,
  RememberedEventMapResponse,
  RememberedEventMutationResponse,
  NearbyEventCountResponse,
  NearbyEventMapResponse,
  NearbyEventsResponse,
  RelatedEventsResponse,
} from '../types/api';
import config from '../config';

export class ApiRequestError extends Error {
  constructor(
    message: string,
    public status: number,
    public code: string,
    public retryAfterSeconds?: number,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

async function requestError(response: Response, fallback: string): Promise<ApiRequestError> {
  const payload = await response.json().catch(() => ({}));
  const retryAfter = Number.parseInt(response.headers.get('Retry-After') || '', 10);
  return new ApiRequestError(
    payload.message || fallback,
    response.status,
    payload.error || 'REQUEST_FAILED',
    Number.isFinite(retryAfter) ? retryAfter : undefined,
  );
}

// Utility function to build query string from object
function buildQueryString(params: Record<string, any>): string {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value.toString());
    }
  });
  return queryParams.toString();
}

/**
 * Transform frontend filters to backend-compatible format
 */
function transformFilters(filters: EventFilters): any {
  const transformed: any = { ...filters };
  
  // Map eventType to type for backend compatibility
  if (transformed.eventType) {
    transformed.type = transformed.eventType;
    delete transformed.eventType;
  }

  // Strip client-only fields the backend doesn't recognize.
  delete transformed.useGeoCity;
  delete transformed.radiusKm;
  delete transformed.horizonDays;

  return transformed;
}

function nearbyRequestBody(
  latitude: number,
  longitude: number,
  radiusKm: number,
  params: URLSearchParams = new URLSearchParams(),
  extra: Record<string, unknown> = {},
) {
  return {
    ...Object.fromEntries(params.entries()),
    ...extra,
    latitude,
    longitude,
    radiusKm,
  };
}

/**
 * EventFinderAPI - Service class for API interactions
 */
class EventFinderAPI {
  /**
   * Get events with pagination and filtering
   */
  static async getEvents(
    filters: EventFilters = {},
    page: number = 1,
    limit: number = config.pagination.eventsDefaultLimit,
    sortBy: string = 'StartDate',
    sortDirection: 'ASC' | 'DESC' = 'ASC'
  ): Promise<EventsResponse> {
    const url = `${config.api.baseUrl}/getEvents`;
    
    // Transform filters for backend compatibility
    const transformedFilters = transformFilters(filters);
    
    // Use POST method as shown in API documentation for complex filters
    const requestBody = {
      filters: transformedFilters,
      page,
      limit,
      sortBy,
      sortDirection,
    };

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        signal: AbortSignal.timeout(config.api.timeout),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching events:', error);
      throw error;
    }
  }

  /**
   * Get events by category using AI-powered semantic search
   */
  static async getEventsByCategory(
    categoryName: string,
    filters: EventFilters = {},
    page: number = 1,
    limit: number = config.pagination.eventsDefaultLimit,
    sortBy: string = 'relevanceScore',
    sortDirection: 'ASC' | 'DESC' = 'DESC'
  ): Promise<EventsResponse> {
    const url = `${config.api.baseUrl}/getEventsByCategory`;
    
    // Transform filters for backend compatibility
    const transformedFilters = transformFilters(filters);
    
    const requestBody = {
      categoryName,
      filters: transformedFilters,
      page,
      limit,
      sortBy,
      sortDirection,
    };

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        signal: AbortSignal.timeout(config.api.timeout),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching events by category:', error);
      throw error;
    }
  }

  /**
   * Get events with POST method for complex filters
   */
  static async searchEvents(
    filters: EventFilters = {},
    page: number = 1,
    limit: number = config.pagination.eventsDefaultLimit
  ): Promise<EventsResponse> {
    const url = `${config.api.baseUrl}/getEvents`;
    
    const requestBody = {
      filters,
      page,
      limit,
    };

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        signal: AbortSignal.timeout(config.api.timeout),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error searching events:', error);
      throw error;
    }
  }

  /**
   * Get partners with pagination and filtering
   */
  static async getPartners(
    entityType?: string,
    searchTerm?: string,
    hasUpcomingEvents?: boolean,
    page: number = 1,
    limit: number = config.pagination.partnersDefaultLimit
  ): Promise<PartnersResponse> {
    const url = `${config.api.baseUrl}/getPartners`;
    const params = new URLSearchParams();
    
    if (entityType) params.append('entityType', entityType);
    if (searchTerm) params.append('searchTerm', searchTerm);
    if (hasUpcomingEvents !== undefined) params.append('hasUpcomingEvents', hasUpcomingEvents.toString());
    params.append('page', page.toString());
    params.append('limit', limit.toString());

    try {
      const response = await fetch(`${url}?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: AbortSignal.timeout(config.api.timeout),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching partners:', error);
      throw error;
    }
  }

  /**
   * Get locations with grouping and filtering
   */
  static async getLocations(
    groupBy: 'city' | 'state' | 'country' = 'city',
    country?: string,
    state?: string,
    upcomingOnly?: boolean,
    limit: number = config.pagination.locationsDefaultLimit
  ): Promise<LocationsResponse> {
    const url = `${config.api.baseUrl}/getLocations`;
    const params = new URLSearchParams();
    
    params.append('groupBy', groupBy);
    if (country) params.append('country', country);
    if (state) params.append('state', state);
    if (upcomingOnly !== undefined) params.append('upcomingOnly', upcomingOnly.toString());
    params.append('limit', limit.toString());

    try {
      const response = await fetch(`${url}?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: AbortSignal.timeout(config.api.timeout),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching locations:', error);
      throw error;
    }
  }

  /**
   * Get event categories with AI-powered semantic search
   */
  static async getEventCategories(
    filters: {
      entityName?: string;
      entityType?: string;
      location?: string;
      state?: string;
      country?: string;
      startDate?: string;
      endDate?: string;
      categoryName?: string;
    } = {},
    limit: number = config.search.defaultLimit,
    includeExamples: boolean = true
  ): Promise<EventCategoriesResponse> {
    const url = `${config.api.baseUrl}/getEventCategories`;
    const params = new URLSearchParams();
    
    // Add filter parameters
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
    
    params.append('limit', limit.toString());
    params.append('includeExamples', includeExamples.toString());

    try {
      const response = await fetch(`${url}?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: AbortSignal.timeout(config.api.timeout),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching event categories:', error);
      throw error;
    }
  }

  /**
   * Search for events near a coordinate using the dedicated
   * `getEventsNearMe` endpoint, which prefers each event's venue street-level
   * coordinates and falls back to the event Location when the venue isn't
   * geocoded yet. Results are returned in the standardized EventInfo
   * envelope (each event includes `coordinates` and `distanceKm`).
   *
   * Paginates internally up to `maxResults` (the endpoint caps `limit` at
   * 100). Events are returned ordered by ascending distance.
   */
  static async searchEventsNearby(
    latitude: number,
    longitude: number,
    radiusKm: number = 15,
    maxResults: number = 500,
  ): Promise<Event[]> {
    const collected: Event[] = [];
    let page = 1;
    while (collected.length < maxResults) {
      const result = await this.getNearbyEvents(
        latitude,
        longitude,
        radiusKm,
        new URLSearchParams({ upcomingOnly: 'true', sortBy: 'distance', sortDirection: 'ASC' }),
        page,
        100,
      );
      collected.push(...result.events);
      if (!result.pagination.hasNextPage || result.events.length === 0) break;
      page += 1;
    }
    return collected.slice(0, maxResults);
  }

  static async getNearbyEvents(
    latitude: number,
    longitude: number,
    radiusKm: number,
    params: URLSearchParams = new URLSearchParams(),
    page: number = 1,
    limit: number = 100,
    signal?: AbortSignal,
  ): Promise<NearbyEventsResponse> {
    const response = await fetch(`${config.api.baseUrl}/getEventsNearMe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(nearbyRequestBody(latitude, longitude, radiusKm, params, { page, limit })),
      signal: signal ?? AbortSignal.timeout(config.api.timeout),
    });
    if (!response.ok) throw await requestError(response, 'Events Near Me could not be loaded');
    return response.json();
  }

  static async getNearbyEventMap(
    latitude: number,
    longitude: number,
    radiusKm: number,
    params: URLSearchParams = new URLSearchParams(),
    signal?: AbortSignal,
  ): Promise<NearbyEventMapResponse> {
    const response = await fetch(`${config.api.baseUrl}/getEventsNearMe/map`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(nearbyRequestBody(latitude, longitude, radiusKm, params)),
      signal: signal ?? AbortSignal.timeout(config.api.timeout),
    });
    if (!response.ok) throw await requestError(response, 'The Events Near Me map could not be loaded');
    return response.json();
  }

  static async getNearbyEventCount(
    latitude: number,
    longitude: number,
    options: { radiusKm?: number; days?: number; excludeEventId?: number; signal?: AbortSignal } = {},
  ): Promise<NearbyEventCountResponse> {
    const response = await fetch(`${config.api.baseUrl}/getEventsNearMe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(nearbyRequestBody(latitude, longitude, options.radiusKm ?? 15, new URLSearchParams(), {
        days: options.days ?? 30,
        excludeEventId: options.excludeEventId,
        countOnly: true,
      })),
      signal: options.signal ?? AbortSignal.timeout(config.api.timeout),
    });
    if (!response.ok) throw await requestError(response, 'Nearby event count could not be loaded');
    return response.json();
  }

  static async getNearbyEventsExport(
    latitude: number,
    longitude: number,
    radiusKm: number,
    format: 'csv' | 'markdown' | 'text',
    params: URLSearchParams = new URLSearchParams(),
  ): Promise<Blob> {
    const response = await fetch(`${config.api.baseUrl}/getEventsNearMe/export`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(nearbyRequestBody(latitude, longitude, radiusKm, params, { exportFormat: format })),
      signal: AbortSignal.timeout(config.api.timeout),
    });
    if (!response.ok) throw await requestError(response, 'The Events Near Me export could not be created');
    return response.blob();
  }

  /**
   * Search for events near a *named* city using the new `getEventsByCity`
   * endpoint, which resolves the city via the curated `worldcities`
   * reference dataset and runs a true geo-radius query against the events
   * table — so events in adjacent municipalities are also returned.
   *
   * Optional `state` / `country` disambiguate cities that share a name
   * (e.g. multiple "Springfield"s). Pagination follows `getEventsNearMe`'s
   * envelope: each page caps at 100 results and we paginate up to
   * `maxResults` total. Results are ordered by ascending distance by
   * default and every event includes `distanceKm` from the resolved city
   * center.
   */
  static async searchEventsByCity(
    city: string,
    options: {
      state?: string;
      country?: string;
      radiusKm?: number;
      upcomingOnly?: boolean;
      days?: number;
      maxResults?: number;
      sortBy?: 'distance' | 'startDate';
      sortDirection?: 'ASC' | 'DESC';
    } = {}
  ): Promise<Event[]> {
    const {
      state,
      country,
      radiusKm = 50,
      upcomingOnly = true,
      days = 30,
      maxResults = 500,
      sortBy = 'distance',
      sortDirection = 'ASC',
    } = options;

    const pageSize = 100;
    const url = `${config.api.baseUrl}/getEventsByCity`;

    const fetchPage = async (page: number): Promise<{ events: Event[]; hasNext: boolean }> => {
      const params = new URLSearchParams({
        city,
        radiusKm: radiusKm.toString(),
        upcomingOnly: upcomingOnly.toString(),
        days: days.toString(),
        sortBy,
        sortDirection,
        page: page.toString(),
        limit: pageSize.toString(),
      });
      if (state) params.append('state', state);
      if (country) params.append('country', country);

      const res = await fetch(`${url}?${params}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: AbortSignal.timeout(config.api.timeout),
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`getEventsByCity ${res.status}: ${body || res.statusText}`);
      }
      const data = await res.json();
      const events: Event[] = Array.isArray(data?.events) ? data.events : [];
      const hasNext = !!data?.pagination?.hasNextPage;
      return { events, hasNext };
    };

    const collected: Event[] = [];
    let page = 1;
    while (collected.length < maxResults) {
      const { events, hasNext } = await fetchPage(page);
      collected.push(...events);
      if (!hasNext || events.length === 0) break;
      page += 1;
    }
    return collected.slice(0, maxResults);
  }

  /**
   * Get a single page of events near a named city. Use this when the caller
   * wants direct control over pagination (e.g. an infinite-scroll list)
   * rather than the cumulative `searchEventsByCity` helper above.
   */
  static async getEventsByCity(
    city: string,
    options: {
      state?: string;
      country?: string;
      radiusKm?: number;
      upcomingOnly?: boolean;
      days?: number;
      isVirtual?: boolean;
      page?: number;
      limit?: number;
      sortBy?: 'distance' | 'startDate';
      sortDirection?: 'ASC' | 'DESC';
      filters?: URLSearchParams;
      signal?: AbortSignal;
    } = {}
  ): Promise<EventsResponse & { facets?: NearbyEventsResponse['facets'] }> {
    const {
      state,
      country,
      radiusKm = 50,
      upcomingOnly = true,
      days = 30,
      isVirtual,
      page = 1,
      limit = 20,
      sortBy = 'distance',
      sortDirection = 'ASC',
      filters = new URLSearchParams(),
      signal,
    } = options;

    const url = `${config.api.baseUrl}/getEventsByCity`;
    const params = new URLSearchParams(filters);
    params.set('city', city);
    params.set('radiusKm', radiusKm.toString());
    params.set('upcomingOnly', upcomingOnly.toString());
    if (!params.has('days')) params.set('days', days.toString());
    params.set('sortBy', sortBy);
    params.set('sortDirection', sortDirection);
    params.set('page', page.toString());
    params.set('limit', limit.toString());
    if (state) params.set('state', state);
    if (country) params.set('country', country);
    if (isVirtual !== undefined) params.set('isVirtual', isVirtual.toString());

    const res = await fetch(`${url}?${params}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      signal: signal ?? AbortSignal.timeout(config.api.timeout),
    });
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(`getEventsByCity ${res.status}: ${body || res.statusText}`);
    }
    return res.json();
  }

  /**
   * Perform semantic search for events
   */
  static async vectorSearch(request: VectorSearchRequest, signal?: AbortSignal): Promise<VectorSearchResponse> {
    const url = `${config.api.baseUrl}/vectorSearch`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
        signal: signal ?? AbortSignal.timeout(config.api.timeout),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error performing vector search:', error);
      throw error;
    }
  }

  /**
   * Get location suggestions based on search input.
   *
   * `signal` lets the caller cancel an in-flight typeahead request when the
   * query changes, so a slow earlier response can never overwrite a newer one.
   */
  static async locationSuggest(
    query: string,
    limit: number = config.search.locationLimit,
    signal?: AbortSignal,
  ): Promise<LocationSuggestion[]> {
    const url = `${config.api.baseUrl}/locationSuggest`;
    const params = new URLSearchParams({
      q: query,
      limit: limit.toString(),
    });

    const response = await fetch(`${url}?${params}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      signal: signal ?? AbortSignal.timeout(config.api.timeout),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.suggestions || [];
  }

  /**
   * Get entity suggestions based on search input.
   *
   * City entities are excluded server-side because they duplicate the curated
   * `locations.json` suggestions; they are filtered again here as defense in
   * depth so a stale API can never reintroduce them.
   */
  static async entitySuggest(
    query: string, 
    limit: number = config.search.entityLimit,
    entityType?: string,
    includeEventCounts: boolean = true,
    signal?: AbortSignal,
    entityTypes?: string[],
  ): Promise<EntitySuggestion[]> {
    const url = `${config.api.baseUrl}/entitySuggest`;
    const params = new URLSearchParams({
      q: query,
      limit: limit.toString(),
      includeEventCounts: includeEventCounts.toString(),
    });

    if (entityType) {
      params.append('entityType', entityType);
    }
    if (entityTypes?.length) {
      params.append('entityTypes', entityTypes.join(','));
    }

    const response = await fetch(`${url}?${params}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      signal: signal ?? AbortSignal.timeout(config.api.timeout),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    const suggestions: EntitySuggestion[] = data.suggestions || [];
    return suggestions.filter((s) => (s.entityType || '').toLowerCase() !== 'city');
  }

  static async eventGroupEntitySuggest(
    query: string,
    limit: number = 10,
    signal?: AbortSignal,
  ): Promise<EntitySuggestion[]> {
    return this.entitySuggest(query, limit, undefined, true, signal, ['Partner', 'Company']);
  }

  static async getEventGroupChallenge(signal?: AbortSignal): Promise<EventGroupChallenge> {
    const response = await fetch(`${config.api.baseUrl}/event-groups/challenge`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      signal: signal ?? AbortSignal.timeout(config.api.timeout),
    });
    if (!response.ok) throw await requestError(response, 'Event Group creation is unavailable');
    return response.json();
  }

  static async createEventGroup(
    request: CreateEventGroupRequest,
    signal?: AbortSignal,
  ): Promise<CreateEventGroupResponse> {
    const response = await fetch(`${config.api.baseUrl}/event-groups`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
      signal: signal ?? AbortSignal.timeout(config.api.timeout),
    });
    if (!response.ok) throw await requestError(response, 'The Event Group could not be created');
    return response.json();
  }

  static async getEventGroup(slug: string, signal?: AbortSignal): Promise<EventGroup | null> {
    const response = await fetch(`${config.api.baseUrl}/event-groups/${encodeURIComponent(slug)}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      signal: signal ?? AbortSignal.timeout(config.api.timeout),
    });
    if (response.status === 404) return null;
    if (!response.ok) throw await requestError(response, 'The Event Group could not be loaded');
    const payload = await response.json();
    return payload.group;
  }

  static async getEventGroupEvents(
    slug: string,
    params: URLSearchParams,
    signal?: AbortSignal,
  ): Promise<EventGroupEventsResponse> {
    const response = await fetch(
      `${config.api.baseUrl}/event-groups/${encodeURIComponent(slug)}/events?${params}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: signal ?? AbortSignal.timeout(config.api.timeout),
      },
    );
    if (!response.ok) throw await requestError(response, 'The Event Group events could not be loaded');
    return response.json();
  }

  static async getEventGroupMap(
    slug: string,
    params: URLSearchParams,
    signal?: AbortSignal,
  ): Promise<EventGroupMapResponse> {
    const response = await fetch(
      `${config.api.baseUrl}/event-groups/${encodeURIComponent(slug)}/map?${params}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: signal ?? AbortSignal.timeout(config.api.timeout),
      },
    );
    if (!response.ok) throw await requestError(response, 'The Event Group map could not be loaded');
    return response.json();
  }

  static eventGroupExportUrl(slug: string, format: 'csv' | 'markdown' | 'text', params: URLSearchParams): string {
    const exportParams = new URLSearchParams(params);
    exportParams.delete('page');
    exportParams.set('exportFormat', format);
    return `${config.api.baseUrl}/event-groups/${encodeURIComponent(slug)}/export?${exportParams}`;
  }

  static async getRememberedEventIds(
    browserId: string,
    signal?: AbortSignal,
  ): Promise<RememberedEventIdsResponse> {
    const response = await fetch(`${config.api.baseUrl}/remembered-events/ids`, {
      method: 'GET',
      headers: { 'x-eventfinder-browser-id': browserId },
      signal: signal ?? AbortSignal.timeout(config.api.timeout),
    });
    if (!response.ok) throw await requestError(response, 'Remembered events could not be loaded');
    return response.json();
  }

  static async rememberEvent(browserId: string, eventId: number): Promise<RememberedEventMutationResponse> {
    const response = await fetch(`${config.api.baseUrl}/remembered-events/${eventId}`, {
      method: 'PUT',
      headers: { 'x-eventfinder-browser-id': browserId },
      signal: AbortSignal.timeout(config.api.timeout),
    });
    if (!response.ok) throw await requestError(response, 'The event could not be remembered');
    return response.json();
  }

  static async forgetRememberedEvent(browserId: string, eventId: number): Promise<RememberedEventMutationResponse> {
    const response = await fetch(`${config.api.baseUrl}/remembered-events/${eventId}`, {
      method: 'DELETE',
      headers: { 'x-eventfinder-browser-id': browserId },
      signal: AbortSignal.timeout(config.api.timeout),
    });
    if (!response.ok) throw await requestError(response, 'The event could not be removed from My Events');
    return response.json();
  }

  static async forgetPastRememberedEvents(browserId: string): Promise<ForgetPastRememberedEventsResponse> {
    const response = await fetch(`${config.api.baseUrl}/remembered-events/past`, {
      method: 'DELETE',
      headers: { 'x-eventfinder-browser-id': browserId },
      signal: AbortSignal.timeout(config.api.timeout),
    });
    if (!response.ok) throw await requestError(response, 'Past events could not be removed');
    return response.json();
  }

  static async getRememberedEvents(
    browserId: string,
    params: URLSearchParams,
    signal?: AbortSignal,
  ): Promise<RememberedEventEventsResponse> {
    const response = await fetch(`${config.api.baseUrl}/remembered-events?${params}`, {
      method: 'GET',
      headers: { 'x-eventfinder-browser-id': browserId },
      signal: signal ?? AbortSignal.timeout(config.api.timeout),
    });
    if (!response.ok) throw await requestError(response, 'My Events could not be loaded');
    return response.json();
  }

  static async getRememberedEventMap(
    browserId: string,
    params: URLSearchParams,
    signal?: AbortSignal,
  ): Promise<RememberedEventMapResponse> {
    const response = await fetch(`${config.api.baseUrl}/remembered-events/map?${params}`, {
      method: 'GET',
      headers: { 'x-eventfinder-browser-id': browserId },
      signal: signal ?? AbortSignal.timeout(config.api.timeout),
    });
    if (!response.ok) throw await requestError(response, 'The My Events map could not be loaded');
    return response.json();
  }

  static async getRememberedEventsExport(
    browserId: string,
    format: 'csv' | 'markdown' | 'text',
    params: URLSearchParams,
  ): Promise<Blob> {
    const exportParams = new URLSearchParams(params);
    exportParams.delete('page');
    exportParams.set('exportFormat', format);
    const response = await fetch(`${config.api.baseUrl}/remembered-events/export?${exportParams}`, {
      method: 'GET',
      headers: { 'x-eventfinder-browser-id': browserId },
      signal: AbortSignal.timeout(config.api.timeout),
    });
    if (!response.ok) throw await requestError(response, 'The My Events export could not be created');
    return response.blob();
  }

  /**
   * Resolve a canonical entity route (`/<type>/<slug>/events`), a legacy
   * `?entity=<name>` link, or a bare entity id. The response carries
   * `redirectTo` whenever the requested URL is not the canonical one.
   */
  static async resolveEntity(
    query: { entityType: string; slug: string } | { entityId: number } | { entityName: string },
    signal?: AbortSignal,
  ): Promise<ResolveEntityResponse | null> {
    const params = new URLSearchParams();
    if ('entityId' in query) {
      params.set('entityId', String(query.entityId));
    } else if ('entityName' in query) {
      params.set('entityName', query.entityName);
    } else {
      params.set('entityType', query.entityType);
      params.set('slug', query.slug);
    }

    const response = await fetch(`${config.api.baseUrl}/resolveEntity?${params}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      signal: signal ?? AbortSignal.timeout(config.api.timeout),
    });

    if (response.status === 404) return null;
    if (!response.ok) {
      throw new Error(`resolveEntity ${response.status}: ${response.statusText}`);
    }
    return response.json();
  }

  /**
   * Resolve a canonical location route (`/location/<slug>/events`) or a legacy
   * `?city=&state=&country=` link to a curated city with coordinates.
   */
  static async resolveLocation(
    query: { slug: string } | { city: string; state?: string; country?: string },
    signal?: AbortSignal,
  ): Promise<ResolveLocationResponse | null> {
    const params = new URLSearchParams();
    if ('slug' in query) {
      params.set('slug', query.slug);
    } else {
      params.set('city', query.city);
      if (query.state) params.set('state', query.state);
      if (query.country) params.set('country', query.country);
    }

    const response = await fetch(`${config.api.baseUrl}/resolveLocation?${params}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      signal: signal ?? AbortSignal.timeout(config.api.timeout),
    });

    if (response.status === 404) return null;
    if (!response.ok) {
      throw new Error(`resolveLocation ${response.status}: ${response.statusText}`);
    }
    return response.json();
  }

  /**
   * Every event for one entity in a single request — upcoming plus anything
   * that ended within `recentPastDays`. There is no pagination in this release,
   * so the caller asks for the full window and reads `pagination.totalItems`
   * to detect (and warn about) truncation.
   */
  static async getEntityEvents(
    entityId: number,
    options: { limit?: number; recentPastDays?: number; recentlyAddedDays?: number; signal?: AbortSignal } = {},
  ): Promise<EventsResponse> {
    const {
      limit = config.pagination.entityEventsLimit,
      recentPastDays = 14,
      recentlyAddedDays = 7,
      signal,
    } = options;

    const response = await fetch(`${config.api.baseUrl}/getEvents`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        filters: { entityId, recentPastDays, recentlyAddedDays },
        page: 1,
        limit,
        sortBy: 'StartDate',
        sortDirection: 'ASC',
      }),
      signal: signal ?? AbortSignal.timeout(config.api.timeout),
    });

    if (!response.ok) {
      throw new Error(`getEvents ${response.status}: ${response.statusText}`);
    }
    return response.json();
  }

  /**
   * Geo-radius search from an explicit coordinate — used after a location
   * suggestion is chosen so the search runs from the curated city coordinates
   * rather than matching on the city *name*.
   */
  static async getEventsNearCoordinates(
    latitude: number,
    longitude: number,
    options: {
      radiusKm?: number;
      page?: number;
      limit?: number;
      upcomingOnly?: boolean;
      days?: number;
      signal?: AbortSignal;
    } = {},
  ): Promise<EventsResponse & { query?: unknown }> {
    const {
      radiusKm = 50,
      page = 1,
      limit = config.pagination.entityEventsLimit,
      upcomingOnly = true,
      days = 30,
      signal,
    } = options;

    const params = new URLSearchParams({
      latitude: latitude.toString(),
      longitude: longitude.toString(),
      radiusKm: radiusKm.toString(),
      upcomingOnly: upcomingOnly.toString(),
      days: days.toString(),
      // Geographic results are ordered by ascending start date per the UX spec;
      // distance still ships on every event for display.
      sortBy: 'startDate',
      sortDirection: 'ASC',
      page: page.toString(),
      limit: Math.min(limit, 100).toString(),
    });

    const response = await fetch(`${config.api.baseUrl}/getEventsNearMe?${params}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      signal: signal ?? AbortSignal.timeout(config.api.timeout),
    });

    if (!response.ok) {
      throw new Error(`getEventsNearMe ${response.status}: ${response.statusText}`);
    }
    return response.json();
  }

  /**
   * Discover events with various discovery types
   */
  static async discoverEvents(
    type: 'popular' | 'trending' | 'upcoming' | 'new' | 'nearby' | 'recommendations' = 'popular',
    timeframe?: string,
    interests?: string,
    page: number = 1,
    limit: number = config.search.defaultLimit
  ): Promise<DiscoverEventsResponse> {
    const queryParams = buildQueryString({
      type,
      timeframe,
      interests,
      page,
      limit,
    });

    const response = await fetch(
      `${config.api.baseUrl}/discoverEvents?${queryParams}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: AbortSignal.timeout(config.api.timeout),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to discover events: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get system insights and analytics
   */
  static async getSystemInsights(
    entityName?: string,
    state?: string,
    timeframe?: string
  ): Promise<SystemInsights> {
    const queryParams = buildQueryString({
      entityName,
      state,
      timeframe,
    });

    const response = await fetch(
      `${config.api.baseUrl}/getSystemInsights?${queryParams}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: AbortSignal.timeout(config.api.timeout),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to get system insights: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Search events with typeahead functionality
   * Uses vector search with optimized parameters for quick suggestions
   */
  static async searchTypeahead(
    searchTerms: string,
    limit: number = config.search.typeaheadLimit
  ): Promise<VectorSearchResponse> {
    return this.vectorSearch({
      searchTerms,
      limit,
      threshold: config.search.vectorThreshold,
      includeEventDetails: true,
      filters: {
        upcomingOnly: true, // Focus on upcoming events for typeahead
      },
    });
  }

  /**
   * Get combined search suggestions for both events and locations
   */
  static async getSearchSuggestions(
    searchTerms: string,
    eventLimit: number = config.search.eventLimit,
    locationLimit: number = config.search.locationLimit,
    entityLimit: number = config.search.entityLimit
  ): Promise<SearchSuggestionsResponse> {
    try {
      // Execute all three searches in parallel
      const [eventResults, locationResults, entityResults] = await Promise.all([
        this.searchTypeahead(searchTerms, eventLimit),
        this.locationSuggest(searchTerms, locationLimit),
        this.entitySuggest(searchTerms, entityLimit),
      ]);

      return {
        events: eventResults.results,
        locations: locationResults,
        entities: entityResults,
      };
    } catch (error) {
      console.error('Error getting search suggestions:', error);
      return {
        events: [],
        locations: [],
        entities: [],
      };
    }
  }

  /**
   * Top cities by upcoming event count, already resolved against the curated
   * `locations.json` dataset. Each entry carries a canonical location URL, so
   * different places that share a name (London, Ontario vs London, UK) are
   * never merged — and cities outside the curated dataset are omitted rather
   * than folded into a same-named one.
   */
  static async getTopCities(
    limit: number = config.pagination.topCitiesLimit,
    signal?: AbortSignal,
  ): Promise<TopCity[]> {
    const params = new URLSearchParams({ limit: limit.toString() });
    const response = await fetch(`${config.api.baseUrl}/getTopCities?${params}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      signal: signal ?? AbortSignal.timeout(config.api.timeout),
    });

    if (!response.ok) {
      throw new Error(`getTopCities ${response.status}: ${response.statusText}`);
    }

    const data: TopCitiesResponse = await response.json();
    return Array.isArray(data.cities) ? data.cities : [];
  }

  static async getAppMetadata(signal?: AbortSignal): Promise<AppMetadata | null> {
    const response = await fetch(`${config.api.baseUrl}/getAppMetadata`, {
      method: 'GET',
      signal: signal ?? AbortSignal.timeout(config.api.timeout),
    });

    if (response.status === 404 || response.status === 503) return null;
    if (!response.ok) {
      throw new Error(`getAppMetadata ${response.status}: ${response.statusText}`);
    }
    return response.json();
  }

  /** Get entity-ranked popular events for the homepage. */
  static async getPopularEvents(limit: number = config.pagination.popularEventsLimit): Promise<DiscoverEventsResponse> {
    return this.discoverEvents('popular', undefined, undefined, 1, Math.min(limit, 18));
  }

  /** Compatibility alias for older clients. */
  static async getTrendingEvents(limit: number = config.pagination.popularEventsLimit): Promise<DiscoverEventsResponse> {
    return this.discoverEvents('trending', undefined, undefined, 1, Math.min(limit, 18));
  }

  /**
   * Get upcoming events by location
   */
  static async getEventsByLocation(
    city: string,
    state?: string,
    limit: number = config.pagination.eventsDefaultLimit
  ): Promise<EventsResponse> {
    return this.getEvents(
      {
        city,
        state,
        upcomingOnly: true,
      },
      1,
      limit
    );
  }

  /**
   * Get event details by ID with enhanced information
   */
  static async getEventDetails(eventId: number): Promise<EventInfo> {
    const response = await fetch(
      `${config.api.baseUrl}/getEventDetails/${eventId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: AbortSignal.timeout(config.api.timeout),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to get event details: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get event by ID - fallback method with error handling
   */
  static async getEvent(eventId: number): Promise<EventsResponse> {
    try {
      // Use the event details API instead of filters since eventId is not in EventFilters
      const eventDetails = await this.getEventDetails(eventId);
      // Convert EventInfo to EventsResponse format
      const event: any = {
        eventId: eventDetails.eventId,
        title: eventDetails.title,
        description: eventDetails.description,
        startDate: eventDetails.startDate,
        endDate: eventDetails.endDate,
        city: eventDetails.city,
        state: eventDetails.state,
        country: eventDetails.country,
        venue: eventDetails.venue,
        isVirtual: eventDetails.isVirtual,
        isPartnerHosted: eventDetails.isPartnerHosted,
        eventUrl: eventDetails.eventUrl,
        eventSources: eventDetails.eventSources,
        entity: eventDetails.entity,
      };
      
      return {
        pagination: {
          page: 1,
          limit: 1,
          totalItems: 1,
          totalPages: 1,
          hasNextPage: false,
          hasPreviousPage: false,
        },
        filters: {},
        sort: { field: 'StartDate', direction: 'ASC' },
        events: [event],
      };
    } catch (error) {
      console.error('Error fetching event:', error);
      // Return empty response structure in case of error
      return {
        pagination: {
          page: 1,
          limit: 1,
          totalItems: 0,
          totalPages: 0,
          hasNextPage: false,
          hasPreviousPage: false,
        },
        filters: {},
        sort: { field: 'StartDate', direction: 'ASC' },
        events: [],
      };
    }
  }

  /**
   * Get detailed event information by ID
   * This method calls the actual /api/getEventInfo endpoint
   */
  static async getEventInfo(eventId: number): Promise<EventInfo> {
    const bootstrap = document.getElementById('event-page-data');
    if (bootstrap) {
      bootstrap.remove();
      try {
        const initialEvent = JSON.parse(bootstrap.textContent || 'null');
        if (initialEvent?.eventId === eventId && typeof initialEvent.title === 'string') {
          return initialEvent as EventInfo;
        }
      } catch {}
    }
    const url = `${config.api.baseUrl}/getEventInfo`;
    const params = new URLSearchParams({
      eventId: eventId.toString()
    });

    try {
      const response = await fetch(`${url}?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: AbortSignal.timeout(config.api.timeout),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching event info:', error);
      throw error;
    }
  }

  /**
   * Get similar and same-timeframe events for an event detail page.
   */
  static async getRelatedEvents(
    eventId: number,
    options: { limit?: number; signal?: AbortSignal } = {}
  ): Promise<RelatedEventsResponse> {
    const params = new URLSearchParams({ eventId: eventId.toString() });
    if (options.limit) params.set('limit', options.limit.toString());

    const response = await fetch(`${config.api.baseUrl}/getRelatedEvents?${params}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      signal: options.signal ?? AbortSignal.timeout(config.api.timeout),
    });

    if (!response.ok) {
      throw await requestError(response, 'Failed to load related events');
    }

    return response.json();
  }
}

export default EventFinderAPI;
